/**
 * Prop Studio's Look Lab: where the render looks are made (room3d/renderLooks.ts).
 *
 * Down the left: the room to try looks on (a designer preset, or a saved
 * estimate's own room, opened by its link or id — or ?estimate=<guid> in the
 * address), the space the looks are for, and that space's looks: on/off,
 * edit, rename, duplicate, delete, each with its last test picture. In the
 * middle, the room in the full 3D designer, without the designer's own look
 * and Photoreal buttons, its brand bar or its materials readout: the page
 * names the room, and looks are not about quantities. Either sidebar folds
 * to a strip (remembered) to give the view the width. On the right, the look being made: every Photoreal
 * setting Blender renders with (PhotoRenderBody, previewed live in the 3D
 * view), saved as a new look or over the one being edited, and a Test render
 * that queues the room with these settings on this site's render queue
 * (render-jobs.php lab-submit) and shows its pictures as they land.
 *
 * The looks are the site's (the props library's `looks`): every customer
 * render of a space is given one of its enabled looks by the server.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronsLeft, ChevronsRight, Copy, ImageIcon, Pencil, Plus, Save, Trash2, Type } from 'lucide-react';
import { DesignerHost, DesignerHostApi, designerControlStyle as control } from '../designer/DesignerHost';
import { usePropLibrary } from '../designer/usePropLibrary';
import { DESIGNER_PRESETS, DEFAULT_PRESET_ID, getPreset } from '../designer/presets';
import { DesignerDocument, designerDocument } from '../designer/designerDocument';
import { planFromSingleRoom, setCeilingProfile } from '../../calculations/plan';
import { SPACE_TYPES } from '../../data/presets';
import { PhotoRenderBody } from '../designer/render/PhotoRenderBody';
import { useRenderSettings } from '../designer/render/useRenderSettings';
import { previewRenderSettings } from '../ui/room3d/renderSettings';
import { photoPreviewSettings } from '../ui/room3d/photoPreview';
import { setLensPreview } from '../ui/room3d/lensPreview';
import { FACTORY_PHOTO_RENDER, PhotoRenderSettings } from '../ui/room3d/photoRender';
import { LOOK_MAX, LookPools, RenderLook, newLookId } from '../ui/room3d/renderLooks';
import { LIBRARY_CHANGED_EVENT, ServiceDownError, getLibrary, saveLooks } from './studioApi';
import { fetchLabRenderStatus, loadEstimateByGuid, submitLabRender } from '../../services/api';
import type { RenderStatus } from '../results/render/types';
import { progressLine } from '../results/render/RenderProgress';
import { useTranslation } from 'react-i18next';
import { PROP_STUDIO_STRINGS } from './strings';
import '../designer/render/renderControls.css';

const L = PROP_STUDIO_STRINGS.lookLab;
const ACCENT = '#FEB100';
const MUTED = '#a9abb3';
const SPACE_KEY = 'propStudio.lookSpace';
const PANELS_KEY = 'propStudio.lookLab.panels';
const GUID =/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

const spaceName = (id: string) => SPACE_TYPES.find((s) => s.id === id)?.label ?? id;
const sameSettings = (a: PhotoRenderSettings, b: PhotoRenderSettings) => JSON.stringify(a) === JSON.stringify(b);

/** A preset's room, as the designer sandbox opens it. */
function presetDocument(id: string): DesignerDocument {
  const doc = getPreset(id).build();
  let plan = planFromSingleRoom(doc.dims, doc.room);
  if (doc.ceilingProfile) {
    const shaped = setCeilingProfile(plan, [plan.rooms[0].id], doc.ceilingProfile);
    if (shaped.ok) plan = shaped.plan;
  }
  return designerDocument(plan, plan.rooms[0].id);
}

type Room =
  | { kind: 'preset'; id: string }
  | { kind: 'estimate'; guid: string; name: string; doc: DesignerDocument; space?: string };

interface Test { guid: string; status: RenderStatus | null; error: string | null }

const card: React.CSSProperties = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, overflow: 'hidden' };
const iconBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, height: 24, padding: '0 7px', borderRadius: 5, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 11, cursor: 'pointer' };
const label: React.CSSProperties = { fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, margin: '12px 0 6px' };
const foldBtn: React.CSSProperties = { ...iconBtn, width: 24, padding: 0, justifyContent: 'center', flex: '0 0 auto' };

/** Which sidebars are open; remembered between visits. */
interface Panels { left: boolean; right: boolean }
const readPanels = (): Panels => {
  try {
    const raw = window.localStorage.getItem(PANELS_KEY);
    const p = raw ? JSON.parse(raw) : null;
    return { left: p?.left !== false, right: p?.right !== false };
  } catch { return { left: true, right: true }; }
};

/** A sidebar folded to a strip: one button to open it, and its name down the strip. */
const FoldedPanel: React.FC<{ side: 'left' | 'right'; name: string; title: string; onOpen: () => void }> = ({ side, name, title, onOpen }) => (
  <aside style={{ width: 28, flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '8px 0', background: '#1f1f24', [side === 'left' ? 'borderRight' : 'borderLeft']: '1px solid rgba(255,255,255,0.1)' }}>
    <button type="button" style={foldBtn} title={title} aria-label={title} onClick={onOpen}>
      {side === 'left' ? <ChevronsRight size={13} /> : <ChevronsLeft size={13} />}
    </button>
    <span style={{ writingMode: 'vertical-rl', fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.6, cursor: 'pointer', userSelect: 'none' }} onClick={onOpen}>{name}</span>
  </aside>
);

export const LookLab: React.FC = () => {
  const { t } = useTranslation('results');
  const { variants: propVariants, catalogue, finishes } = usePropLibrary();
  const apiRef = useRef<DesignerHostApi | null>(null);

  // ---- the looks
  const [pools, setPools] = useState<LookPools | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => {
    getLibrary().then((lib) => setPools(lib.looks ?? {})).catch((err) => { setPools({}); setError(err instanceof ServiceDownError ? L.noBackEnd : err instanceof Error ? err.message : String(err)); });
  }, []);
  const save = useCallback(async (next: LookPools) => {
    setBusy(true);
    setError(null);
    try {
      const saved = await saveLooks(next);
      setPools(saved);
      window.dispatchEvent(new Event(LIBRARY_CHANGED_EVENT));
      setNote(L.savedNote);
      return saved;
    } catch (err) {
      setError(err instanceof ServiceDownError ? L.noBackEnd : err instanceof Error ? err.message : String(err));
      return null;
    } finally {
      setBusy(false);
    }
  }, []);

  // ---- the space, and the look being made
  const [space, setSpace] = useState<string>(() => { try { return window.localStorage.getItem(SPACE_KEY) || 'garage'; } catch { return 'garage'; } });
  useEffect(() => { try { window.localStorage.setItem(SPACE_KEY, space); } catch { /* not kept — fine */ } }, [space]);
  const looks = useMemo(() => pools?.[space] ?? [], [pools, space]);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<PhotoRenderSettings>(FACTORY_PHOTO_RENDER);
  const edited = looks.find((l) => l.id === editing) ?? null;
  const dirty = !sameSettings(draft, edited ? edited.photo : FACTORY_PHOTO_RENDER);
  const leaveDraft = () => !dirty || window.confirm(L.discardConfirm);

  const chooseSpace = useCallback((id: string) => { setSpace(id); setEditing(null); setNote(null); }, []);
  // A space with looks opens on its first enabled one (so the room shows it at
  // once); once per space, so New look and Start over are left alone after.
  const openedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!pools || openedFor.current === space) return;
    openedFor.current = space;
    const list = pools[space] ?? [];
    const first = list.find((l) => l.enabled) ?? list[0];
    if (first) { setEditing(first.id); setDraft(first.photo); }
  }, [pools, space]);
  const editLook = (l: RenderLook) => { if (!leaveDraft()) return; setEditing(l.id); setDraft(l.photo); setNote(null); };
  const startOver = () => { if (!leaveDraft()) return; setEditing(null); setDraft(FACTORY_PHOTO_RENDER); setNote(null); };
  const withSpace = (list: RenderLook[]): LookPools => {
    const next = { ...(pools ?? {}) };
    if (list.length) next[space] = list; else delete next[space];
    return next;
  };
  const stamp = () => new Date().toISOString();

  const saveNew = async () => {
    if (looks.length >= LOOK_MAX) { setError(L.full(LOOK_MAX)); return; }
    const name = window.prompt(L.saveNewPrompt(spaceName(space)))?.trim();
    if (!name) return;
    const id = newLookId(name, looks.map((l) => l.id));
    if (await save(withSpace([...looks, { id, name, enabled: true, photo: draft, savedAt: stamp() }]))) setEditing(id);
  };
  const saveEdited = () => edited && save(withSpace(looks.map((l) => (l.id === edited.id ? { ...l, photo: draft, savedAt: stamp() } : l))));
  const toggle = (l: RenderLook) => save(withSpace(looks.map((x) => (x.id === l.id ? { ...x, enabled: !x.enabled } : x))));
  const rename = (l: RenderLook) => {
    const name = window.prompt(L.renamePrompt, l.name)?.trim();
    if (name && name !== l.name) save(withSpace(looks.map((x) => (x.id === l.id ? { ...x, name } : x))));
  };
  const duplicate = (l: RenderLook) => {
    if (looks.length >= LOOK_MAX) { setError(L.full(LOOK_MAX)); return; }
    const name = `${l.name} copy`;
    save(withSpace([...looks, { ...l, id: newLookId(name, looks.map((x) => x.id)), name, savedAt: stamp() }]));
  };
  const remove = async (l: RenderLook) => {
    if (!window.confirm(L.removeConfirm(l.name))) return;
    if (await save(withSpace(looks.filter((x) => x.id !== l.id))) && editing === l.id) setEditing(null);
  };
  // This panel's Effects tab onto every saved look of every space (their lighting,
  // camera, materials and textures untouched): one lens and grade for the whole site.
  const copyEffects = async () => {
    const all = Object.values(pools ?? {}).reduce((n, list) => n + list.length, 0);
    if (!all) { setNote(L.copyEffectsNone); return; }
    if (!window.confirm(L.copyEffectsConfirm(all))) return;
    const next: LookPools = {};
    for (const [s, list] of Object.entries(pools ?? {})) next[s] = list.map((l) => ({ ...l, photo: { ...l.photo, effects: { ...draft.effects } }, savedAt: stamp() }));
    if (await save(next)) setNote(L.copiedEffects(all));
  };

  // ---- the room
  const [room, setRoom] = useState<Room>({ kind: 'preset', id: DEFAULT_PRESET_ID });
  const [roomKey, setRoomKey] = useState(0);
  const [estimateInput, setEstimateInput] = useState('');
  const [roomNote, setRoomNote] = useState<string | null>(null);
  const openEstimate = useCallback(async (text: string) => {
    const guid = GUID.exec(text)?.[0]?.toLowerCase();
    if (!guid) return;
    setRoomNote(L.estimateLoading);
    try {
      const data = await loadEstimateByGuid(guid);
      const designer = data.estimateOutput?.designer;
      if (!designer?.plan) { setRoomNote(L.estimateNotDesigned); return; }
      setRoom({ kind: 'estimate', guid, name: data.estimateName || guid, doc: designerDocument(designer.plan, designer.focusRoomId), space: designer.plan.spaceType });
      setRoomKey((k) => k + 1);
      setRoomNote(null);
      if (designer.plan.spaceType && SPACE_TYPES.some((s) => s.id === designer.plan.spaceType)) chooseSpace(designer.plan.spaceType);
    } catch (err) {
      setRoomNote(L.estimateNotFound(err instanceof Error ? err.message : String(err)));
    }
  }, [chooseSpace]);
  // ?estimate=<guid>: open that estimate's room (the admin's "Open in Look Lab").
  useEffect(() => {
    const asked = new URLSearchParams(window.location.search).get('estimate');
    if (asked) openEstimate(asked);
  }, [openEstimate]);
  const choosePreset = (id: string) => { setRoom({ kind: 'preset', id }); setRoomKey((k) => k + 1); setRoomNote(null); };
  const doc = useMemo(() => (room.kind === 'preset' ? presetDocument(room.id) : room.doc), [room]);

  // ---- the live preview of the draft in the 3D view (as the Photoreal window does it)
  const view = useRenderSettings();
  const [preview, setPreview] = useState(true);
  useEffect(() => { previewRenderSettings(preview ? photoPreviewSettings(view, draft) : null); }, [preview, view, draft]);
  const previewMm = preview && draft.camera.lensType === 'perspective' ? draft.camera.focalMm : null;
  useEffect(() => { setLensPreview(previewMm); }, [previewMm]);
  useEffect(() => () => { previewRenderSettings(null); setLensPreview(null); }, []);

  // ---- test renders: the view you are looking through (quick), or the customer's whole set
  const [testViews, setTestViews] = useState<'user' | 'all+pano'>('user');
  const [test, setTest] = useState<Test | null>(null);
  const testRender = async (): Promise<string> => {
    const cap = await apiRef.current?.capture?.({ width: 1280, height: 720, asSeen: testViews === 'user' });
    if (!cap?.glb || !cap.manifest) return L.testNotReady;
    const queued = await submitLabRender({ glb: cap.glb, manifest: { ...cap.manifest, space, render: draft } }, testViews);
    setTest({ guid: queued.guid, status: null, error: null });
    return L.testQueued(queued.queuePosition);
  };
  useEffect(() => {
    if (!test?.guid) return undefined;
    let stop = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const status = await fetchLabRenderStatus(test.guid);
        if (stop) return;
        setTest((t) => (t && t.guid === test.guid ? { ...t, status, error: null } : t));
        if (status.pollMs > 0) timer = setTimeout(poll, status.pollMs);
      } catch (err) {
        if (stop) return;
        setTest((t) => (t && t.guid === test.guid ? { ...t, error: err instanceof Error ? err.message : String(err) } : t));
        timer = setTimeout(poll, 5000);
      }
    };
    poll();
    return () => { stop = true; if (timer) clearTimeout(timer); };
  }, [test?.guid]);
  const setPicture = (url: string) => edited && save(withSpace(looks.map((l) => (l.id === edited.id ? { ...l, picture: url } : l))));

  // ---- the sidebars fold away to give the 3D view the width (the viewer follows its box)
  const [panels, setPanels] = useState<Panels>(readPanels);
  useEffect(() => { try { window.localStorage.setItem(PANELS_KEY, JSON.stringify(panels)); } catch { /* not kept — fine */ } }, [panels]);
  const fold = (side: keyof Panels, open: boolean) => setPanels((p) => ({ ...p, [side]: open }));

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
      {/* ---- left: room, space, the space's looks */}
      {!panels.left && <FoldedPanel side="left" name={L.leftTab} title={L.showLeft} onOpen={() => fold('left', true)} />}
      <aside style={{ width: 270, flex: '0 0 auto', overflowY: 'auto', borderRight: '1px solid rgba(255,255,255,0.1)', padding: 10, fontSize: 12, display: panels.left ? 'block' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ ...label, flex: 1, margin: '2px 0 6px' }} title={L.roomTitle}>{L.room}</div>
          <button type="button" style={foldBtn} title={L.hideLeft} aria-label={L.hideLeft} onClick={() => fold('left', false)}><ChevronsLeft size={13} /></button>
        </div>
        <select
          value={room.kind === 'preset' ? room.id : 'estimate'} onChange={(e) => e.target.value !== 'estimate' && choosePreset(e.target.value)}
          style={{ ...control, width: '100%' }} title={L.roomTitle}
        >
          {room.kind === 'estimate' && <option value="estimate" style={{ color: '#000' }}>{L.estimateRoom(room.name)}</option>}
          {DESIGNER_PRESETS.map((p) => <option key={p.id} value={p.id} style={{ color: '#000' }}>{p.label}</option>)}
        </select>
        <form style={{ display: 'flex', gap: 4, marginTop: 6 }} onSubmit={(e) => { e.preventDefault(); openEstimate(estimateInput); }}>
          <input value={estimateInput} onChange={(e) => setEstimateInput(e.target.value)} placeholder={L.estimatePlaceholder} style={{ ...control, flex: 1, minWidth: 0 }} aria-label={L.estimate} />
          <button type="submit" style={{ ...control, cursor: 'pointer' }} disabled={!GUID.test(estimateInput)}>{L.openEstimate}</button>
        </form>
        {roomNote && <p style={{ color: MUTED, margin: '6px 0 0' }}>{roomNote}</p>}

        <div style={label} title={L.spaceTitle}>{L.space}</div>
        <select value={space} onChange={(e) => chooseSpace(e.target.value)} style={{ ...control, width: '100%' }} title={L.spaceTitle}>
          {SPACE_TYPES.map((s) => <option key={s.id} value={s.id} style={{ color: '#000' }}>{s.label} ({pools?.[s.id]?.length ?? 0})</option>)}
        </select>

        <div style={label}>{L.looksOf(spaceName(space))}</div>
        <p style={{ color: MUTED, margin: '0 0 8px', lineHeight: 1.4 }}>{looks.length ? L.intro : L.none(spaceName(space))}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {looks.map((l) => (
            <div key={l.id} style={{ ...card, borderColor: editing === l.id ? ACCENT : 'rgba(255,255,255,0.12)', opacity: l.enabled ? 1 : 0.6 }}>
              {l.picture
                ? <img src={l.picture} alt="" style={{ display: 'block', width: '100%', aspectRatio: '16 / 9', objectFit: 'cover' }} />
                : <div style={{ aspectRatio: '16 / 9', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'repeating-linear-gradient(45deg,#2a2b31 0 8px,#24252a 8px 16px)', color: MUTED }}><ImageIcon size={18} /></div>}
              <div style={{ padding: '6px 8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <strong style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.name}>{l.name}</strong>
                  {editing === l.id && <span style={{ color: ACCENT, fontSize: 10 }}>{L.editing}</span>}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, margin: 0, cursor: 'pointer' }} title={l.enabled ? L.onTitle : L.offTitle}>
                    <input className="rc-switch" type="checkbox" checked={l.enabled} disabled={busy} onChange={() => toggle(l)} />
                    {l.enabled ? L.on : L.off}
                  </label>
                </div>
                {l.savedAt && <div style={{ color: MUTED, fontSize: 10, marginTop: 2 }}>{L.saved(new Date(l.savedAt).toLocaleString())}</div>}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                  <button type="button" style={iconBtn} title={L.editTitle} onClick={() => editLook(l)}><Pencil size={11} /> {L.edit}</button>
                  <button type="button" style={iconBtn} disabled={busy} onClick={() => rename(l)}><Type size={11} /> {L.rename}</button>
                  <button type="button" style={iconBtn} disabled={busy} onClick={() => duplicate(l)}><Copy size={11} /> {L.duplicate}</button>
                  <button type="button" style={iconBtn} disabled={busy} onClick={() => remove(l)}><Trash2 size={11} /> {L.remove}</button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {error && <p style={{ color: '#ff8a80', margin: '10px 0 0' }}>{error}</p>}
      </aside>

      {/* ---- middle: the room */}
      <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
        <DesignerHost
          key={roomKey}
          initial={doc}
          title={room.kind === 'estimate' ? room.name : getPreset(room.id).label}
          apiRef={apiRef}
          initialSpaceType={space}
          stagingSeed={room.kind === 'estimate' ? room.guid : undefined}
          propVariants={propVariants}
          catalogue={catalogue}
          finishes={finishes}
          renderControls={false}
          brandBar={false}
          readout={false}
          embedded
        />
      </div>

      {/* ---- right: the look being made */}
      <aside style={{ width: 400, flex: '0 0 auto', display: panels.right ? 'flex' : 'none', flexDirection: 'column', borderLeft: '1px solid rgba(255,255,255,0.1)', background: '#1f1f24', minHeight: 0 }}>
        <div style={{ padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.1)', fontSize: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ flex: 1, minWidth: 0, fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{L.panelTitle}</div>
            <button type="button" style={foldBtn} title={L.hideRight} aria-label={L.hideRight} onClick={() => fold('right', false)}><ChevronsRight size={13} /></button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
            <strong style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {edited ? L.panelEditing(edited.name) : L.panelNew}
            </strong>
            {dirty && <span style={{ color: ACCENT, fontSize: 10 }}>{L.unsaved}</span>}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {edited && (
              <button type="button" className="rc-action is-active" disabled={busy || !dirty} onClick={saveEdited}><Save size={13} /> {L.update(edited.name)}</button>
            )}
            <button type="button" className={`rc-action${edited ? '' : ' is-active'}`} disabled={busy} onClick={saveNew}><Plus size={13} /> {L.saveNew}</button>
            <button type="button" className="rc-action" disabled={busy} title={L.startOverTitle} onClick={startOver}>{L.startOver}</button>
            <button type="button" className="rc-action" disabled={busy} title={L.copyEffectsTitle} onClick={copyEffects}><Copy size={13} /> {L.copyEffects}</button>
          </div>
          {(busy || note) && <p style={{ color: MUTED, margin: '6px 0 0' }}>{busy ? L.saving : note}</p>}
          <div role="radiogroup" aria-label={L.testViews} title={L.testViewsHint} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
            <span style={{ color: MUTED }}>{L.testViews}:</span>
            {(['user', 'all+pano'] as const).map((v) => (
              <button
                key={v} type="button" role="radio" aria-checked={testViews === v} className={`rc-mode-btn${testViews === v ? ' is-active' : ''}`}
                onClick={() => setTestViews(v)}
              >
                {v === 'user' ? L.testViewUser : L.testViewAll}
              </button>
            ))}
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <PhotoRenderBody
            settings={draft} onChange={setDraft} seamStrength={view.seamStrength}
            onRender={testRender} renderLabel={L.testTitle} preview={preview} onPreview={setPreview} bySpace={false}
            look={{ warmth: view.interiorWarmth, intensity: view.interiorIntensity, ambient: view.ambientIntensity, environment: view.environment, exposure: view.exposure, seamStrength: view.seamStrength }}
          />
          {test && (
            <div style={{ padding: '0 10px 12px', fontSize: 12 }}>
              <div style={label}>{L.testTitle}</div>
              {test.error && <p style={{ color: '#ff8a80', margin: 0 }}>{test.error}</p>}
              {test.status?.job && (
                <div style={{ margin: '0 0 8px' }}>
                  {/* The estimate page's own line (RenderProgress): the step, the %, the time left. */}
                  <p style={{ color: '#fff', margin: '0 0 4px' }}>{progressLine(t, test.status.job, { delayed: false, checking: false }) ?? L.testStatus(test.status.job.status, test.status.job.percent)}</p>
                  <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.12)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${test.status.job.status === 'done' ? 100 : Math.max(0, Math.min(100, test.status.job.percent))}%`, background: ACCENT, transition: 'width 0.6s' }} />
                  </div>
                  {test.status.job.worker && <p style={{ color: MUTED, margin: '4px 0 0', fontSize: 11 }}>{L.testWorker(test.status.job.worker)}</p>}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {(test.status?.pictures ?? []).filter((p) => p.done && p.url).map((p) => (
                  <div key={p.id} style={card}>
                    <a href={p.url ?? undefined} target="_blank" rel="noreferrer"><img src={p.thumbUrl ?? p.url ?? undefined} alt={p.name} style={{ display: 'block', width: '100%' }} /></a>
                    {edited && p.id !== '360' && (
                      <button type="button" style={{ ...iconBtn, margin: 4 }} title={L.usePictureTitle} disabled={busy} onClick={() => setPicture(p.url as string)}>{L.usePicture}</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </aside>
      {!panels.right && <FoldedPanel side="right" name={L.rightTab} title={L.showRight} onOpen={() => fold('right', true)} />}
    </div>
  );
};

export default LookLab;
